import React from 'react';
import Icon from '../../../components/AppIcon';

const FeaturedSection = ({ articles, onArticleClick }) => {
  const [mainArticle, ...sideArticles] = articles || [];

  if (!mainArticle) return null;

  return (
    <section className="mb-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center">
            <Icon name="Star" size={20} className="text-white" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-2xl text-accent-800">Featured Articles</h2>
            <p className="text-accent-600">Editor's picks and must-read guides</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Featured Article */}
        <div className="lg:col-span-2">
          <article
            onClick={() => onArticleClick?.(mainArticle)}
            className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group relative"
          >
            {/* Featured Badge */}
            <div className="absolute top-6 left-6 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-full text-sm font-medium z-10">
              <Icon name="Star" size={14} className="inline mr-1" />
              Featured
            </div>

            {/* Article Image */}
            <div className="relative overflow-hidden">
              <img
                src={mainArticle?.image}
                alt={mainArticle?.title}
                className="w-full h-64 lg:h-80 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center space-x-2 mb-3">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium">
                    {mainArticle?.category}
                  </span>
                  <div className="flex items-center space-x-1 text-xs">
                    <Icon name="Clock" size={12} />
                    <span>{mainArticle?.readTime} min read</span>
                  </div>
                </div>
                
                <h3 className="font-heading font-bold text-2xl mb-2 line-clamp-2">
                  {mainArticle?.title}
                </h3>
                
                <p className="text-white/90 text-sm line-clamp-2 mb-4">
                  {mainArticle?.excerpt}
                </p>
                
                {/* Author Info */}
                <div className="flex items-center space-x-3">
                  <img
                    src={mainArticle?.author?.avatar}
                    alt={mainArticle?.author?.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-sm">{mainArticle?.author?.name}</span>
                      {mainArticle?.author?.isVerified && (
                        <Icon name="CheckCircle" size={14} className="text-primary" />
                      )}
                    </div>
                    <span className="text-xs text-white/80">{mainArticle?.author?.credentials}</span>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        {/* Side Articles */}
        <div className="space-y-6">
          {sideArticles?.map((article, index) => (
            <article
              key={article?.id}
              onClick={() => onArticleClick?.(article)}
              className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="flex">
                {/* Article Image */}
                <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden">
                  <img
                    src={article?.image}
                    alt={article?.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Article Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="bg-accent-100 text-accent-700 px-2 py-1 rounded-full text-xs font-medium">
                      {article?.category}
                    </span>
                    <div className="flex items-center space-x-1 text-xs text-accent-500">
                      <Icon name="Clock" size={10} />
                      <span>{article?.readTime}m</span>
                    </div>
                  </div>
                  
                  <h4 className="font-heading font-semibold text-sm text-accent-800 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {article?.title}
                  </h4>
                  
                  <div className="flex items-center space-x-3 text-xs text-accent-500">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={12} />
                      <span>{(article?.views / 1000).toFixed(1)}K</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Heart" size={12} />
                      <span>{article?.likes}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedSection;